import { useEditorStore } from "components/Editor/useEditorStore";
import { LiveIcon } from "components/ShareButton/LiveSessionSettings";
import { useContainer } from "features/container/Feature";
import { useEditorFeature, useSandboxEditor } from "features/editor/Feature";
import { usePitcher } from "features/utils/usePitcher";
import { useSetupProgress } from "hooks/pitcher/useSetupProgress";
import { hasSandboxPermission } from "isomorphic/permission";
import {
  Box,
  ButtonBadge,
  Badge,
  Popover,
  Paragraph,
  Tooltip,
  Stack,
} from "prism-react";

import { canSeamlessBranch } from "utils/canSeamlessBranch";
import { executeVSCodeCommand } from "utils/vscode";

import { useEditorUpdateStatusContent } from "./useEditorUpdateStatusContent";

export const SandboxStatus: React.FC = () => {
  const [editorFeature] = useEditorFeature();
  const editor = useSandboxEditor();
  const [container] = useContainer();
  const setupProgress = useSetupProgress();
  const editorUpdateStatus = useEditorUpdateStatusContent();
  const boxType = editor.isCloud ? "Devbox" : "Sandbox";
  const { liveSession } = useEditorStore();
  const pitcher = usePitcher();

  if (editorFeature.state === "READY" && editorFeature.isSeamlesslyBranching) {
    return (
      <Badge icon="progress" iconSize={4} variant="neutral">
        Seamlessly forking...
      </Badge>
    );
  }

  if (container.state === "RESTARTING") {
    return (
      <Tooltip content="The microVM is restarting and will shortly reconnect and run your setup tasks.">
        <div>
          <Badge icon="progress" iconSize={4} variant="neutral">
            Restarting
          </Badge>
        </div>
      </Tooltip>
    );
  }

  // We have been back and forward on when to show this badge. The reconnect can be slow for different reasons
  // and currently it is better to show it when you are disconnected or not, or the experience can be confusing. It
  // might be an idea to show some other visual indication to avoid catching attention, but at least now
  // the user is always aware of the state of the connection
  if (
    container.state === "RECONNECTING" ||
    container.state === "DISCONNECTED_RECONNECTING"
  ) {
    return (
      <Badge icon="progress" iconSize={4} variant="neutral">
        Reconnecting
      </Badge>
    );
  }

  if (editorUpdateStatus) {
    return editorUpdateStatus;
  }

  if (setupProgress.state === "IN_PROGRESS") {
    return (
      <ButtonBadge
        icon="progress"
        iconSize={4}
        onClick={() => {
          executeVSCodeCommand("codesandbox.openCsbTab", {
            type: "SETUP_TASKS",
          });
        }}
        variant="neutral"
      >
        Setup in progress ({setupProgress.currentStepIndex + 1}/
        {setupProgress.steps.length})
      </ButtonBadge>
    );
  }

  if (setupProgress.state === "STOPPED") {
    return (
      <ButtonBadge
        iconSize={4}
        onClick={() => {
          executeVSCodeCommand("codesandbox.openCsbTab", {
            type: "SETUP_TASKS",
          });
        }}
        variant="error"
      >
        Setup failed ({setupProgress.currentStepIndex + 1}/
        {setupProgress.steps.length})
      </ButtonBadge>
    );
  }

  const liveSessionHost =
    liveSession.state.status === "CONNECTED" &&
    liveSession.state.role === "GUEST"
      ? liveSession.state.session.host?.username
      : null;

  if (liveSessionHost) {
    const client = pitcher.clients.client
      .getClients()
      .find((client) => client.username === liveSessionHost);

    return (
      <Popover
        trigger={
          <ButtonBadge variant="neutral">
            <Stack horizontal>
              <LiveIcon size={3} type="circleFull" connected />
              <span>Live session</span>
            </Stack>
          </ButtonBadge>
        }
      >
        <Box css={{ width: 210 }}>
          <Paragraph>
            You're in a live session as a guest hosted by{" "}
            {client?.name ?? liveSessionHost}.
          </Paragraph>
        </Box>
      </Popover>
    );
  }

  const hasWriteAccess = hasSandboxPermission(
    editor.authorization,
    "write_code",
  );

  if (!hasWriteAccess && !editor.restricted && !canSeamlessBranch(editor)) {
    return (
      <Popover
        trigger={
          <ButtonBadge icon="eyeOpen" iconSize={4} variant="accentSecondary">
            Read only
          </ButtonBadge>
        }
      >
        <Box css={{ width: 210 }}>
          <Paragraph>
            You only have read access to this {boxType}, fork to get access
          </Paragraph>
        </Box>
      </Popover>
    );
  }

  if (editor.isFrozen && hasWriteAccess) {
    return (
      <ButtonBadge
        icon="lockClose"
        onClick={() => {
          executeVSCodeCommand("codesandbox.openCsbTab", {
            type: "SANDBOX_INFO",
            isCloud: editor.isCloud,
          });
        }}
        title={`This ${boxType} will be forked when edited. Click to open ${boxType} info.`}
        variant="accentSecondary"
      >
        Protected
      </ButtonBadge>
    );
  }

  return null;
};
